import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  useTheme,
  useMediaQuery,
  Stack,
  TextField,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  CircularProgress,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  PLAY_STORE_LINK_REGEX,
  APP_STORE_LINK_REGEX,
  APP_STORE_LINK_REGEX_2,
  MONTHLY_MARKETTING_SPENDS_OPTIONS,
  TOAST_TYPES,
  DataAccessProvider,
  EMAIL_REGEX,
  APPLICATION_STORES,
} from '@constants/constants';
import { setToastMessage } from '@stores/App/slice';
import { AdminRegisterUserPayload } from '@api/register';
import useUploadNda from '@hooks/api/useUploadNDA';
import DrawerComponent from '../../../../components/Drawer/Drawer';
import { menuItemStyles } from '@utils/styles';
import { useLazyGetCategoriesAndGenresQuery } from '@api/games';
import uploadIcon from '../../../../assets/icons/upload_black.svg';
import formIcon from '../../../../assets/icons/form.svg';
import deleteIcon from '../../../../assets/icons/delete.svg';
import DataAccessSourceFields from './DataAccessSourceFields';
import { useLazyFetchApplicationDetailsQuery } from '@api/register';
import { processRatingString } from '@utils/common';
import GameDetailsCard, {
  IGameDetails,
} from '../../../Dashboard/GettingStartedDashboards/components/GameData/GameDetails';
import { SelectWithLabel } from '@components';
import { useSelector } from 'react-redux';
import { RootState } from '@stores/store';
import useCompany from '@hooks/api/useCompany';

interface NewGameSidePanelProps {
  showNewGameSidePanel: boolean;
  setShowNewGameSidePanel: React.Dispatch<React.SetStateAction<boolean>>;
  createNewGameAction: (data: AdminRegisterUserPayload) => any;
  onCreateSuccess: () => void;
  isLoading: boolean;
}
interface SelectFieldProps {
  label: string;
  id: string;
  options: { label: string; value: string }[];
  error: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
  value: string;
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
}
const DATA_ACCESS_SOURCE_OPTIONS = [
  {
    label: 'AppsFlyer',
    value: DataAccessProvider.APPS_FLYER,
  },
  {
    label: 'Adjust',
    value: DataAccessProvider.ADJUST,
  },
  {
    label: 'Aggregated Cohort Data',
    value: DataAccessProvider.AGGREGATED_DATA,
  },
  {
    label: 'BigQuery',
    value: DataAccessProvider.BIGQUERY,
  },
  {
    label: 'GCS',
    value: DataAccessProvider.GCS,
  },
  {
    label: 'S3',
    value: DataAccessProvider.S3,
  },
  {
    label: 'MySQL',
    value: DataAccessProvider.MYSQL,
  },
  {
    label: 'Snowflake',
    value: DataAccessProvider.SNOWFLAKE,
  },
  {
    label: 'Direct File',
    value: DataAccessProvider.GOOGLE_DRIVE,
  },
];

const NewGameSidePanel: React.FC<NewGameSidePanelProps> = ({
  showNewGameSidePanel,
  setShowNewGameSidePanel,
  createNewGameAction,
  onCreateSuccess,
  isLoading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { uploadNda, isLoading: isNdaLoading } = useUploadNda();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fetchApplicationDetailsApi] = useLazyFetchApplicationDetailsQuery();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dataAccessSource, setDataAccessSource] = useState<DataAccessProvider | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<{ name: string; genres: string[] }[]>([]);
  const [genreOptions, setGenreOptions] = useState<string[]>([]);
  const [enableCreateGameButton, setEnableCreateGameButton] = useState(false);
  const [playStoreApp, setPlayStoreApp] = useState<null | IGameDetails>(null);
  const [appStoreApp, setAppStoreApp] = useState<null | IGameDetails>(null);
  const [newGameData, setNewGameData] = useState<AdminRegisterUserPayload>({
    dealName: '',
    accountName: '',
    category: '',
    genre: '',
    monthlyMarketingSpend: '',
    playStoreUrl: '',
    appStoreUrl: '',
    iapMargin: '',
    iaaIdList: [],
    mmp: '',
    connectionDetails: null,
    userEmail: '',
    clientCode: '',
    refreshRequired: false,
  });
  const companies = useSelector((state: RootState) => state?.CompanyListData);
  const { getCompanyList } = useCompany();

  const [isFormValid, setIsFormValid] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({
    dealName: false,
    userEmail: false,
    monthlyMarketingSpend: false,
    category: false,
    playStoreUrl: false,
    appStoreUrl: false,
    genre: false,
    mmp: false,
    accountName: false,
    iapMargin: false,
    iaaIdList: false,
    connectionDetails: false,
    clientCode: false,
  });

  useEffect(() => {
    if (!companies?.companyList?.length) getCompanyList();
  }, []);

  const isDealNameValid = useMemo(() => {
    return newGameData.dealName && newGameData.dealName.length > 0;
  }, [newGameData.dealName]);

  const isUserEmailValid = useMemo(() => {
    return newGameData.userEmail && EMAIL_REGEX.test(newGameData.userEmail);
  }, [newGameData.userEmail]);

  const isMonthlyMarketingSpendValid = useMemo(() => {
    return newGameData.monthlyMarketingSpend && newGameData.monthlyMarketingSpend.length > 0;
  }, [newGameData.monthlyMarketingSpend]);

  const isCategoryValid = useMemo(() => {
    return newGameData.category && newGameData.category.length > 0;
  }, [newGameData.category]);

  const isGenreValid = useMemo(() => {
    return newGameData.genre && newGameData.genre.length > 0;
  }, [newGameData.genre]);

  const isMmpValid = useMemo(() => {
    return newGameData.mmp && newGameData.mmp.length > 0;
  }, [newGameData.mmp]);

  const isPlayStoreLinkValid = useMemo(() => {
    if (
      !newGameData.playStoreUrl &&
      newGameData.appStoreUrl &&
      (APP_STORE_LINK_REGEX.test(newGameData.appStoreUrl) ||
        APP_STORE_LINK_REGEX_2.test(newGameData.appStoreUrl))
    ) {
      return true;
    }

    return newGameData.playStoreUrl && PLAY_STORE_LINK_REGEX.test(newGameData.playStoreUrl);
  }, [newGameData.playStoreUrl, newGameData.appStoreUrl]);

  const isAppStoreLinkValid = useMemo(() => {
    if (
      !newGameData.appStoreUrl &&
      newGameData.playStoreUrl &&
      PLAY_STORE_LINK_REGEX.test(newGameData.playStoreUrl)
    ) {
      return true;
    }

    return (
      newGameData.appStoreUrl &&
      (APP_STORE_LINK_REGEX.test(newGameData.appStoreUrl) ||
        APP_STORE_LINK_REGEX_2.test(newGameData.appStoreUrl))
    );
  }, [newGameData.appStoreUrl, newGameData.playStoreUrl]);

  const isClientCodeValid = useMemo(() => {
    return newGameData.clientCode && newGameData.clientCode.length > 0;
  }, [newGameData.clientCode]);

  const [fetchCategoryAndGenreOptionsApi] = useLazyGetCategoriesAndGenresQuery();

  // Hook for fetching the category and genre options.
  useEffect(() => {
    fetchCategoryAndGenreOptions();
  }, []);

  // Hook for updating genre and genre options when category is changed.
  useEffect(() => {
    if (!newGameData?.category) return;

    let genres: string[] = [];
    for (const cat of categoryOptions) {
      if (cat.name !== newGameData.category) continue;

      genres = cat.genres;
      break;
    }

    setGenreOptions(genres);
  }, [newGameData?.category]);

  // Hook to reset accountName, iapMargin and iaaIdList when mmp is changed.
  useEffect(() => {
    setNewGameData((prevState) => ({
      ...prevState,
      accountName: '',
      iapMargin: '',
      iaaIdList: [],
      connectionDetails: null,
    }));
  }, [newGameData.mmp]);

  // Hook to validate the game form when newGameData is updated.
  useEffect(() => {
    validateGameForm(newGameData);
  }, [newGameData]);

  const fetchCategoryAndGenreOptions = async (): Promise<void> => {
    try {
      const response = await fetchCategoryAndGenreOptionsApi().unwrap();
      const { data } = response;

      setCategoryOptions(data.categories);
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || null,
        }),
      );
    }
  };

  const validateGameForm = (newGameToUpdate: AdminRegisterUserPayload) => {
    const {
      accountName,
      category,
      genre,
      monthlyMarketingSpend,
      playStoreUrl,
      appStoreUrl,
      iapMargin,
      iaaIdList,
      mmp,
      connectionDetails,
      userEmail,
      clientCode,
    } = newGameToUpdate;

    const isPlayStoreLinkValid = playStoreUrl ? PLAY_STORE_LINK_REGEX.test(playStoreUrl) : true;
    const isAppStoreLinkValid = appStoreUrl
      ? APP_STORE_LINK_REGEX.test(appStoreUrl) || APP_STORE_LINK_REGEX_2.test(appStoreUrl)
      : true;
    const isEmailValid = userEmail ? EMAIL_REGEX.test(userEmail) : false;

    let isInvalid =
      !category ||
      !genre ||
      !monthlyMarketingSpend ||
      !(playStoreUrl || appStoreUrl) ||
      Boolean(playStoreUrl && !isPlayStoreLinkValid) ||
      Boolean(appStoreUrl && !isAppStoreLinkValid) ||
      !mmp ||
      !isEmailValid ||
      !clientCode;

    if (dataAccessSource === DataAccessProvider.APPS_FLYER) {
      isInvalid = isInvalid || !accountName || !iapMargin;
    } else if (dataAccessSource === DataAccessProvider.ADJUST) {
      isInvalid = isInvalid || !accountName || !iapMargin || !iaaIdList || !iaaIdList.length;
    } else {
      isInvalid = isInvalid || !connectionDetails;
    }

    setEnableCreateGameButton(!isInvalid);
  };

  const handleUpdateNewGameData = (key: string, value: string) => {
    let updatedNewGame = { ...newGameData };
    if (key === 'category') {
      updatedNewGame.genre = '';
    }

    updatedNewGame = {
      ...updatedNewGame,
      [key]: value,
    };

    setNewGameData(updatedNewGame);

    if (key === 'appStoreUrl' || key === 'playStoreUrl') {
      if (key === 'appStoreUrl' && value.length === 0) {
        setAppStoreApp(null);
      }
      if (key === 'playStoreUrl' && value.length === 0) {
        setPlayStoreApp(null);
      }

      if (
        key === 'appStoreUrl' &&
        (APP_STORE_LINK_REGEX.test(value) || APP_STORE_LINK_REGEX_2.test(value))
      ) {
        fetchApplicationDetails(value, APPLICATION_STORES.APP_STORE);
      }
      if (key === 'playStoreUrl' && PLAY_STORE_LINK_REGEX.test(value)) {
        fetchApplicationDetails(value, APPLICATION_STORES.PLAY_STORE);
      }
    }
  };

  const fetchApplicationDetails = async (
    appUrl: string,
    store: (typeof APPLICATION_STORES)[keyof typeof APPLICATION_STORES],
  ) => {
    try {
      const { data } = await fetchApplicationDetailsApi({
        store,
        appUrl,
      }).unwrap();
      if (!data) return;
      const appDetails = {
        marketPlace: data.marketPlace,
        appUrl: data.appUrl,
        name: data.name,
        icon: data.icon,
        developerName: data.developerName,
        rating: processRatingString(data.rating),
        reviews: data.reviews,
        downloads: data.downloads,
      };

      store === APPLICATION_STORES.PLAY_STORE
        ? setPlayStoreApp(appDetails)
        : setAppStoreApp(appDetails);
    } catch (error: any) {
      console.error(error);
      store === APPLICATION_STORES.PLAY_STORE ? setPlayStoreApp(null) : setAppStoreApp(null);
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message || 'Unable to fetch application details.',
        }),
      );
    }
  };

  const handleCreateNewGame = useCallback(async () => {
    try {
      const res = await createNewGameAction(newGameData);
      if (!res || !res.data?.id) {
        throw new Error('Game creation failed: No ID returned.');
      }
      if (selectedFile) {
        await handleUploadNda(res.data.id, selectedFile as File);
      }
      if (onCreateSuccess) {
        onCreateSuccess();
      }
    } catch (error) {
      console.error('Failed to create new game:', error);
    }
  }, [newGameData, selectedFile, onCreateSuccess]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUploadClick = () => {
    if (isLoading) return;

    // Trigger the file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUploadNda = async (selfServeId: string, file: File) => {
    if (!selfServeId) {
      console.error('Missing selfServeId for NDA upload');
      return;
    }

    const result = await uploadNda(selfServeId, file);

    if (result.success) {
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSelectedFile(null);
    }
  };

  const renderSelectField = ({
    label,
    id,
    options,
    error,
    onChange,
    onBlur,
    value,
    fullWidth = true,
    required = true,
    disabled = false,
  }: SelectFieldProps) => {
    return (
      <FormControl fullWidth={fullWidth} required={required} error={error}>
        <InputLabel id={id}>{label}</InputLabel>
        <Select
          labelId={id}
          label={label}
          value={value}
          onChange={(e: any) => onChange(e.target.value)}
          onBlur={onBlur}
          fullWidth
          disabled={disabled}
          sx={{
            height: '56px',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {options.map((option, index) => (
            <MenuItem
              key={`${index}--${option.value}`}
              value={option.value}
              sx={{
                ...menuItemStyles,
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <DrawerComponent
      open={showNewGameSidePanel}
      anchor="right"
      close={() => {
        setShowNewGameSidePanel(false);
      }}
      height="98vh"
      zIndex={1300}
      backgroundColor="#F0F0F1"
    >
      <Box
        sx={{
          width: '900px',
          maxWidth: isMobile ? 'min(900px, 95vw)' : '900px',
          height: '100%',
          padding: '1.5rem',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '1.375rem',
            fontWeight: 700,
            lineHeight: '1.875rem',
            color: '#6C6C6C',
            marginBottom: '1.5rem',
          }}
        >
          New Game
          <Button
            sx={{ padding: 0, minWidth: 'fit-content' }}
            onClick={() => {
              setShowNewGameSidePanel(false);
            }}
          >
            <CloseIcon sx={{ color: '#162C36' }} />
          </Button>
        </Box>
        <Stack gap={'1.5rem'}>
          {/* Deal Details */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              width: '100%',
              backgroundColor: '#FFFFFF',
              padding: '1.5rem',
              borderRadius: '0.5rem',
            }}
          >
            <Box>
              <TextField
                id="new-game-deal-name-text-field"
                label="Deal Name"
                placeholder="Deal Name"
                variant="outlined"
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleUpdateNewGameData('dealName', e.target.value)
                }
                value={newGameData.dealName}
                onBlur={() =>
                  setTouchedFields((prev) => ({
                    ...prev,
                    dealName: true,
                  }))
                }
                error={touchedFields.dealName && !isDealNameValid}
                sx={{
                  width: '100%',
                }}
                required
              />
            </Box>
            <Box>
              <TextField
                id="new-game-email-text-field"
                label="Email"
                placeholder="Email"
                variant="outlined"
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleUpdateNewGameData('userEmail', e.target.value)
                }
                onBlur={() =>
                  setTouchedFields((prev) => ({
                    ...prev,
                    userEmail: true,
                  }))
                }
                value={newGameData.userEmail}
                sx={{
                  width: '100%',
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0,
                  },
                }}
                required
                error={touchedFields.userEmail && !isUserEmailValid}
                helperText={
                  touchedFields.userEmail && !isUserEmailValid
                    ? 'Please enter a valid email address'
                    : ''
                }
              />
            </Box>
            <Box>
              {renderSelectField({
                label: 'Monthly Marketing Spends',
                id: 'marketing-spends-field',
                options: MONTHLY_MARKETTING_SPENDS_OPTIONS,
                error: touchedFields.monthlyMarketingSpend && !isMonthlyMarketingSpendValid,
                onChange: (value) => handleUpdateNewGameData('monthlyMarketingSpend', value),
                onBlur: () =>
                  setTouchedFields((prev) => ({
                    ...prev,
                    monthlyMarketingSpend: true,
                  })),
                value: newGameData.monthlyMarketingSpend,
              })}
            </Box>
            <SelectWithLabel
              label="Company"
              required
              field="clientCode"
              labelId="company-select-label"
              menuItems={(companies.companyList ?? []).map((company) => ({
                label: company.name,
                value: company.clientCode || '',
              }))}
              onChange={(value) => {
                handleUpdateNewGameData(
                  'clientCode',
                  typeof value === 'string' ? value : (value.clientCode ?? ''),
                );
              }}
              onBlur={() =>
                setTouchedFields((prev) => ({
                  ...prev,
                  clientCode: true,
                }))
              }
              error={touchedFields.clientCode && !isClientCodeValid}
              isAddNew={true}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 400,
                  },
                },
              }}
              value={newGameData.clientCode}
              fullWidth
              selectStyles={{
                height: '56px',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                borderRadius: '0.25rem',
                paddingRight: '0.5rem',
              }}
            />
          </Box>
          {/* Game Details */}
          <Stack
            gap={'1.5rem'}
            sx={{
              width: '100%',
              backgroundColor: '#FFFFFF',
              padding: '1.5rem',
              borderRadius: '0.5rem',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <TextField
                id="new-game-app-store-text-field"
                label="Link to App Store"
                placeholder="Link to App Store"
                variant="outlined"
                required={!playStoreApp && !appStoreApp}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleUpdateNewGameData('appStoreUrl', e.target.value)
                }
                onBlur={() =>
                  setTouchedFields((prev) => ({
                    ...prev,
                    appStoreUrl: true,
                  }))
                }
                value={newGameData.appStoreUrl}
                error={touchedFields.appStoreUrl && !isAppStoreLinkValid}
                helperText={
                  touchedFields.appStoreUrl && !isAppStoreLinkValid
                    ? 'Please enter a valid App Store link'
                    : ''
                }
                sx={{
                  width: '49%',
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0,
                  },
                }}
              />
              <TextField
                id="new-game-play-store-text-field"
                label="Link to Play Store"
                placeholder="Link to Play Store"
                variant="outlined"
                required={!playStoreApp && !appStoreApp}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  handleUpdateNewGameData('playStoreUrl', e.target.value)
                }
                onBlur={() =>
                  setTouchedFields((prev) => ({
                    ...prev,
                    playStoreUrl: true,
                  }))
                }
                value={newGameData.playStoreUrl}
                error={touchedFields.playStoreUrl && !isPlayStoreLinkValid}
                helperText={
                  touchedFields.playStoreUrl && !isPlayStoreLinkValid
                    ? 'Please enter a valid Play Store link'
                    : ''
                }
                sx={{
                  width: '49%',
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0,
                  },
                }}
              />
            </Box>

            {(appStoreApp || playStoreApp) && (
              <Stack gap={isMobile ? 2 : 1} direction={isMobile ? 'column' : 'row'} flexWrap="wrap">
                {appStoreApp && (
                  <GameDetailsCard
                    showEditIcon={false}
                    setIsEditGameOpen={() => {}}
                    category={null}
                    genre={null}
                    gameDetails={appStoreApp}
                  />
                )}

                {playStoreApp && (
                  <GameDetailsCard
                    showEditIcon={false}
                    setIsEditGameOpen={() => {}}
                    category={null}
                    genre={null}
                    gameDetails={playStoreApp}
                  />
                )}
              </Stack>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Box sx={{ width: '49%' }}>
                {renderSelectField({
                  label: 'Category',
                  id: 'category-field',
                  options: categoryOptions.map((category) => ({
                    label: category.name,
                    value: category.name,
                  })),
                  error: touchedFields.category && !isCategoryValid,
                  onChange: (value) => handleUpdateNewGameData('category', value),
                  onBlur: () =>
                    setTouchedFields((prev) => ({
                      ...prev,
                      category: true,
                    })),
                  value: newGameData.category,
                })}
              </Box>
              <Box sx={{ width: '49%' }}>
                {renderSelectField({
                  label: 'Genre',
                  id: 'genre-field',
                  options: genreOptions.map((genre) => ({
                    label: genre,
                    value: genre,
                  })),
                  error: touchedFields.genre && !isGenreValid,
                  onChange: (value) => handleUpdateNewGameData('genre', value),
                  onBlur: () =>
                    setTouchedFields((prev) => ({
                      ...prev,
                      genre: true,
                    })),
                  value: newGameData.genre,
                  disabled: !newGameData.category,
                })}
              </Box>
            </Box>
          </Stack>
          {/* Data Access */}
          <Stack
            gap={'1rem'}
            sx={{
              width: '100%',
              backgroundColor: '#FFFFFF',
              padding: '1.5rem',
              borderRadius: '0.5rem',
            }}
          >
            {renderSelectField({
              label: 'Select an Account',
              id: 'data-access-source-field',
              options: DATA_ACCESS_SOURCE_OPTIONS,
              error: touchedFields.mmp && !isMmpValid,
              onChange: (value) => {
                setDataAccessSource(value as DataAccessProvider);
                handleUpdateNewGameData('mmp', value);
              },
              onBlur: () => setTouchedFields((prev) => ({ ...prev, mmp: true })),
              value: dataAccessSource ?? '',
              fullWidth: true,
            })}
            <Box sx={{ width: '100%' }}>
              <DataAccessSourceFields
                dataAccessSource={dataAccessSource}
                newGameData={{
                  accountName: newGameData?.accountName || null,
                  iapMargin: newGameData?.iapMargin || null,
                  iaaIdList: newGameData?.iaaIdList || null,
                  connectionDetails: newGameData?.connectionDetails || null,
                  refreshRequired: newGameData?.refreshRequired || false,
                }}
                handleUpdateNewGameData={handleUpdateNewGameData}
                onValidityChange={setIsFormValid}
              />
            </Box>
          </Stack>
          {/* NDA Upload */}
          <Stack
            gap={'1rem'}
            sx={{
              width: '100%',
              backgroundColor: '#FFFFFF',
              padding: '1.5rem',
              borderRadius: '0.5rem',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                textAlign: 'left',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  color: '#162936',
                  marginRight: '0.2rem',
                }}
              >
                Upload NDA Form
              </Typography>
            </Box>
            {selectedFile ? (
              <Box
                sx={{
                  width: '15rem',
                  backgroundColor: '#F0F0F1',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      marginRight: '0.5rem',
                      marginTop: '0.25rem',
                    }}
                  >
                    <img height={16} width={16} src={formIcon} alt="rating-star" />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#162936',
                      marginRight: '0.2rem',
                    }}
                  >
                    NDA
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                  onClick={() => setSelectedFile(null)}
                >
                  <img height={16} width={16} src={deleteIcon} alt="rating-star" />
                </Box>
              </Box>
            ) : (
              <Stack direction={'row'} gap={'0.5rem'} alignItems={'center'}>
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <Box
                  sx={{
                    width: '5rem',
                    height: '2rem',
                    borderRadius: '2rem',
                    border: '1px solid #2F736E',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: '1rem 4rem',
                    cursor: 'pointer',
                  }}
                  onClick={handleUploadClick}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      color: '#162936',
                    }}
                  >
                    Upload
                  </Typography>
                  {isNdaLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <img src={uploadIcon} alt="upload-icon" style={{ userSelect: 'none' }} />
                  )}
                </Box>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Box>
      <Box
        sx={{
          width: isMobile ? 'auto' : '500px',
          padding: '1rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          bottom: '0',
          borderTop: '1px solid #E8EAEB',
          backgroundColor: '#f0f0f1',
        }}
      >
        <Button
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
          variant="contained"
          onClick={handleCreateNewGame}
          disabled={!isFormValid || !enableCreateGameButton || isLoading}
          sx={{
            padding: '0.625rem 1.5rem',
            borderRadius: '100px',
            textTransform: 'none',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            fontWeight: 500,
            background: '#2F736E',
            color: '#fff',
          }}
        >
          {isLoading && <CircularProgress size={20} sx={{ marginRight: '0.5rem' }} />}
          {isLoading ? 'Creating...' : 'Create'}
        </Button>
        <Button
          disableElevation
          disableFocusRipple
          disableRipple
          disableTouchRipple
          onClick={() => setShowNewGameSidePanel(false)}
          disabled={isLoading}
          sx={{
            padding: '0.625rem 1.5rem',
            borderRadius: '100px',
            textTransform: 'none',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            fontWeight: 500,
            background: 'none',
            color: '#51B8B0',
            ':hover': {
              background: 'none',
            },
          }}
        >
          Close
        </Button>
      </Box>
    </DrawerComponent>
  );
};

export default NewGameSidePanel;
